import { Button } from '@1trading/react/components/ui/button';
import { Card, CardContent } from '@1trading/react/components/ui/card';
import { Checkbox, CheckboxCard } from '@1trading/react/components/ui/checkbox';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react/components/ui/select';
import { Textarea } from '@1trading/react/components/ui/textarea';
import { Plus, Trash2 } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { BINARY_ANSWERS } from '@/constants/answers';
import { CONTROLLING_PERSON_TYPES_BY_ENTITY_KIND, ENTITY_KINDS, type EntityKind } from '@/constants/institution';
import { InstitutionFieldsetPerson } from '@/features/institution/components/fieldsets/person';
import { InstitutionPersonDetailsDifferNotice } from '@/features/institution/components/person-details-differ-notice';
import { useInstitutionDetailsV2Answers } from '@/features/institution/hooks/useInstitutionDetails';
import { createInstitutionPerson, withSharedPersonDetailsOf } from '@/features/institution/schemas/person';
import {
  BENEFICIARY_CLASSES_MAX,
  CONTROLLING_PERSONS_MAX,
  getInstitutionControllingPersonCandidates,
  type InstitutionTaxSelfCertification,
  type InstitutionTaxSelfCertificationOrDefaults,
  isControllingPersonsApplicable,
  isEntityKindAsked,
  isLegalArrangementKind,
  withRolesOfKind,
} from '@/features/institution/tax-self-certification/schema';
import { reportTaxResidenceFormatErrors } from '@/features/tax-residency/report';
import { useFormFieldArray } from '@/hooks/use-form-field-array';
import type { FormStepGuard } from '@/hooks/use-form-step-guard';

const InstitutionTaxSelfCertificationFieldsetControllingPersonsLegend = () => {
  const form = useFormContext<InstitutionTaxSelfCertificationOrDefaults>();
  const { t } = useTranslation();
  const entityKind = form.watch('entityKind');

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.taxSelfCertification.controllingPersons.title')}</PageHeadingTitle>
        <PageHeadingDescription>
          {t('institutionDetails.taxSelfCertification.controllingPersons.description', { context: isLegalArrangementKind(entityKind) ? entityKind : undefined })}
        </PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionTaxSelfCertificationFieldsetControllingPersonsLegend.displayName = 'InstitutionTaxSelfCertificationFieldsetControllingPersonsLegend';

const InstitutionTaxSelfCertificationFieldsetControllingPersons = () => {
  const form = useFormContext<InstitutionTaxSelfCertificationOrDefaults>();
  const { fields, append, remove, update } = useFormFieldArray({ control: form.control, name: 'controllingPersons' });
  const {
    fields: beneficiaryClassFields,
    append: appendBeneficiaryClass,
    remove: removeBeneficiaryClass,
  } = useFormFieldArray({ control: form.control, name: 'beneficiaryClasses' });
  const { data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { t } = useTranslation();
  const controllingPersons = form.watch('controllingPersons') ?? [];
  const [legalForm, entityKind] = form.watch(['legalForm', 'entityKind']);
  const arrangementKind = isLegalArrangementKind(entityKind) ? entityKind : undefined;
  const addedPartyIds = new Set(controllingPersons.map(({ partyId }) => partyId));
  const ownershipPeople = getInstitutionControllingPersonCandidates(institutionDetailsAnswers?.ownership, entityKind);
  const candidatesToCopy = ownershipPeople.filter(({ partyId }) => !addedPartyIds.has(partyId));

  // The copy keeps the person's partyId, which marks the same person across sections
  const addControllingPersonFromCandidate = (partyId: string) => {
    const candidate = candidatesToCopy.find((person) => person.partyId === partyId);

    if (candidate) append(candidate, { shouldFocus: false });
  };

  const keepRolesOfKind = (kind: EntityKind) => {
    form.setValue('controllingPersons', withRolesOfKind(controllingPersons, kind));
  };

  return (
    <div className="space-y-6">
      {isEntityKindAsked(legalForm) ? (
        <FormField
          control={form.control}
          name="entityKind"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('institutionDetails.taxSelfCertification.controllingPersons.entityKind')}*</FormLabel>
              <FormControl>
                <RadioGroup
                  defaultValue={field.value}
                  variant="cards"
                  aria-label={t('institutionDetails.taxSelfCertification.controllingPersons.entityKind')}
                  onValueChange={(kind: EntityKind) => {
                    field.onChange(kind);
                    keepRolesOfKind(kind);
                  }}
                >
                  {ENTITY_KINDS.map((kind) => (
                    <FormItem key={kind}>
                      <RadioGroupItemCard variant="outline">
                        <FormControl>
                          <RadioGroupItem value={kind} />
                        </FormControl>
                        <FormLabel contained>{t(`institutionDetails.taxSelfCertification.controllingPersons.entityKinds.${kind}`)}</FormLabel>
                      </RadioGroupItemCard>
                    </FormItem>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
      {arrangementKind ? (
        <FormField
          control={form.control}
          name="hasProtector"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('institutionDetails.taxSelfCertification.controllingPersons.hasProtector', { context: arrangementKind })}*</FormLabel>
              <FormControl>
                <RadioGroup
                  className="grid-cols-2"
                  defaultValue={field.value}
                  variant="cards"
                  aria-label={t('institutionDetails.taxSelfCertification.controllingPersons.hasProtector', { context: arrangementKind })}
                  onValueChange={field.onChange}
                >
                  {BINARY_ANSWERS.map((answer) => (
                    <FormItem key={answer}>
                      <RadioGroupItemCard variant="outline">
                        <FormControl>
                          <RadioGroupItem value={answer} />
                        </FormControl>
                        <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                      </RadioGroupItemCard>
                    </FormItem>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
      {entityKind ? (
        <FormField
          control={form.control}
          name="controllingPersons"
          render={({ fieldState: { error } }) => (
            <FormItem>
              <div className="space-y-6">
                {fields.map((controllingPerson, index) => (
                  <Card key={controllingPerson.id} className="shadow-none" bordered>
                    <CardContent className="pt-6">
                      <fieldset className="relative space-y-6">
                        <legend className="font-medium">
                          {t('institutionDetails.taxSelfCertification.controllingPersons.controllingPerson')} {index + 1}
                        </legend>
                        <Button
                          className="absolute top-[-0.66rem] right-[-0.66rem] !m-0 text-destructive hover:text-destructive"
                          size="icon"
                          type="button"
                          variant="ghost"
                          onClick={() => remove(index)}
                        >
                          <Trash2 className="size-4" />
                          <span className="sr-only">
                            {t('institutionDetails.taxSelfCertification.controllingPersons.remove')} {index + 1}
                          </span>
                        </Button>
                        <InstitutionPersonDetailsDifferNotice<InstitutionTaxSelfCertification['controllingPersons'][number]>
                          name={`controllingPersons.${index}`}
                          source={ownershipPeople.find(({ partyId }) => partyId === controllingPerson.partyId)}
                          section="ownership"
                          onUseSource={(copy, source) => update(index, withSharedPersonDetailsOf(copy, source))}
                        />
                        <InstitutionFieldsetPerson<InstitutionTaxSelfCertificationOrDefaults> isContactable={false} name={`controllingPersons.${index}`} />
                        <FormField
                          control={form.control}
                          name={`controllingPersons.${index}.isPep`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('institutionDetails.taxSelfCertification.controllingPersons.isPep')}*</FormLabel>
                              <FormDescription>{t('institutionDetails.person.isPepDescription')}</FormDescription>
                              <FormControl>
                                <RadioGroup
                                  className="grid-cols-2"
                                  defaultValue={field.value}
                                  variant="cards"
                                  aria-label={t('institutionDetails.taxSelfCertification.controllingPersons.isPep')}
                                  onValueChange={field.onChange}
                                >
                                  {BINARY_ANSWERS.map((answer) => (
                                    <FormItem key={answer}>
                                      <RadioGroupItemCard variant="outline">
                                        <FormControl>
                                          <RadioGroupItem value={answer} />
                                        </FormControl>
                                        <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                                      </RadioGroupItemCard>
                                    </FormItem>
                                  ))}
                                </RadioGroup>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`controllingPersons.${index}.controllingPersonTypes`}
                          render={() => (
                            <FormItem>
                              <FormLabel>{t('institutionDetails.taxSelfCertification.controllingPersons.controllingPersonTypes', { context: arrangementKind })}*</FormLabel>
                              <FormControl aria-label={t('institutionDetails.taxSelfCertification.controllingPersons.controllingPersonTypes', { context: arrangementKind })}>
                                {/* biome-ignore lint/a11y/useSemanticElements: Makeshift checkboxgroup since it's not a native role */}
                                <div className="space-y-4" role="group">
                                  {CONTROLLING_PERSON_TYPES_BY_ENTITY_KIND[entityKind].map((type) => (
                                    <FormField
                                      key={type}
                                      control={form.control}
                                      name={`controllingPersons.${index}.controllingPersonTypes`}
                                      render={({ field }) => (
                                        <FormItem>
                                          <CheckboxCard variant="outline">
                                            <FormControl>
                                              <Checkbox
                                                checked={field.value?.includes(type)}
                                                variant="cards"
                                                onCheckedChange={(checked) =>
                                                  field.onChange(checked ? [...(field.value ?? []), type] : field.value?.filter((value) => value !== type))
                                                }
                                              />
                                            </FormControl>
                                            <FormLabel contained>{t(`institutionDetails.taxSelfCertification.controllingPersons.controllingPersonTypeOptions.${type}`)}</FormLabel>
                                          </CheckboxCard>
                                        </FormItem>
                                      )}
                                    />
                                  ))}
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </fieldset>
                    </CardContent>
                  </Card>
                ))}
                {fields.length < CONTROLLING_PERSONS_MAX ? (
                  <div className="flex flex-wrap gap-2">
                    {candidatesToCopy.length ? (
                      <Select value="" onValueChange={addControllingPersonFromCandidate}>
                        <SelectTrigger className="h-9 w-auto gap-2" aria-label={t('institutionDetails.taxSelfCertification.controllingPersons.addFromOwnership')}>
                          <SelectValue placeholder={t('institutionDetails.taxSelfCertification.controllingPersons.addFromOwnership')} />
                        </SelectTrigger>
                        <SelectContent>
                          {candidatesToCopy.map((candidate) => (
                            <SelectItem key={candidate.partyId} value={candidate.partyId}>
                              {candidate.firstName} {candidate.lastName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : null}
                    <Button className="gap-2" size="sm" type="button" variant="outline" onClick={() => append({ ...createInstitutionPerson(), controllingPersonTypes: [] })}>
                      <Plus className="size-4" />
                      {t('institutionDetails.taxSelfCertification.controllingPersons.add')}
                    </Button>
                  </div>
                ) : null}
              </div>
              {error?.root?.message || error?.message ? <FormMessage>{error.root?.message ?? error.message}</FormMessage> : null}
            </FormItem>
          )}
        />
      ) : null}
      {arrangementKind ? (
        <fieldset className="space-y-4">
          <legend className="font-medium">{t('institutionDetails.taxSelfCertification.controllingPersons.beneficiaryClasses')}</legend>
          <p className="text-xs text-muted-foreground">{t('institutionDetails.taxSelfCertification.controllingPersons.beneficiaryClassesDescription')}</p>
          {beneficiaryClassFields.map((beneficiaryClass, index) => (
            <FormField
              key={beneficiaryClass.id}
              control={form.control}
              name={`beneficiaryClasses.${index}.description`}
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between gap-2">
                    <FormLabel>
                      {t('institutionDetails.taxSelfCertification.controllingPersons.beneficiaryClass')} {index + 1}*
                    </FormLabel>
                    <Button className="text-destructive hover:text-destructive" size="icon" type="button" variant="ghost" onClick={() => removeBeneficiaryClass(index)}>
                      <Trash2 className="size-4" />
                      <span className="sr-only">
                        {t('institutionDetails.taxSelfCertification.controllingPersons.removeBeneficiaryClass')} {index + 1}
                      </span>
                    </Button>
                  </div>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
          {beneficiaryClassFields.length < BENEFICIARY_CLASSES_MAX ? (
            <Button className="gap-2" size="sm" type="button" variant="outline" onClick={() => appendBeneficiaryClass({ description: '' })}>
              <Plus className="size-4" />
              {t('institutionDetails.taxSelfCertification.controllingPersons.addBeneficiaryClass')}
            </Button>
          ) : null}
        </fieldset>
      ) : null}
    </div>
  );
};
InstitutionTaxSelfCertificationFieldsetControllingPersons.displayName = 'InstitutionTaxSelfCertificationFieldsetControllingPersons';

const beforeStepEnter: FormStepGuard<InstitutionTaxSelfCertification> = async ({ direction, form, steps }, next) => {
  if (!form) return next(false);

  if (!isControllingPersonsApplicable(form.getValues())) {
    return next(steps.indexOf(InstitutionTaxSelfCertificationFieldsetControllingPersons) + (direction === 'next' ? 1 : -1));
  }

  return next();
};

InstitutionTaxSelfCertificationFieldsetControllingPersons.beforeStepEnter = beforeStepEnter;

const beforeStepLeave: FormStepGuard<InstitutionTaxSelfCertification> = async ({ direction, form }, next) => {
  if (direction === 'next') await reportTaxResidenceFormatErrors(form.getValues('controllingPersons').flatMap(({ taxResidences }) => taxResidences));

  return next();
};

InstitutionTaxSelfCertificationFieldsetControllingPersons.beforeStepLeave = beforeStepLeave;

export { InstitutionTaxSelfCertificationFieldsetControllingPersons, InstitutionTaxSelfCertificationFieldsetControllingPersonsLegend };
